import { useEffect, useMemo, useRef, useState } from "react";
import { geoAlbersUsa, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { motion } from "framer-motion";

const TOPO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const W = 975, H = 610;

export default function USMap({ markers = [], dark = false, onMarkerClick }) {
  const [geo, setGeo] = useState(null);
  const [hover, setHover] = useState(null);
  const cache = useRef(null);

  useEffect(() => {
    if (cache.current) { setGeo(cache.current); return; }
    fetch(TOPO_URL)
      .then((r) => r.json())
      .then((topo) => {
        const states = feature(topo, topo.objects.states);
        cache.current = states;
        setGeo(states);
      })
      .catch(() => {});
  }, []);

  const projection = useMemo(() => geoAlbersUsa().scale(1300).translate([W / 2, H / 2]), []);
  const path = useMemo(() => geoPath(projection), [projection]);

  const maxCount = Math.max(...markers.map((m) => m.count), 1);
  const points = markers
    .map((m) => {
      const p = projection([m.lng, m.lat]);
      return p ? { ...m, x: p[0], y: p[1], r: 5 + (m.count / maxCount) * 20 } : null;
    })
    .filter(Boolean);

  const stateFill = dark ? "#161616" : "#EEF2F8";
  const stateStroke = dark ? "#262626" : "#DCE4F0";

  return (
    <div className="relative w-full" data-testid="us-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full">
        {geo && geo.features.map((f, i) => (
          <path key={i} d={path(f)} fill={stateFill} stroke={stateStroke} strokeWidth={1} />
        ))}
        {points.map((m, i) => (
          <g key={m.label} className="cursor-pointer"
            onMouseEnter={() => setHover(m)} onMouseLeave={() => setHover(null)}
            onClick={() => onMarkerClick?.(m)}>
            <circle cx={m.x} cy={m.y} r={m.r} fill="rgba(0,85,255,0.15)">
              <animate attributeName="r" values={`${m.r};${m.r * 1.5};${m.r}`} dur="3s" repeatCount="indefinite" begin={`${i * 0.2}s`} />
              <animate attributeName="opacity" values="0.5;0;0.5" dur="3s" repeatCount="indefinite" begin={`${i * 0.2}s`} />
            </circle>
            <circle cx={m.x} cy={m.y} r={Math.max(4, m.r * 0.35)} fill="#0055FF" stroke="#fff" strokeWidth={dark ? 0 : 1.5} />
          </g>
        ))}
      </svg>

      {hover && (() => {
        const p = projection([hover.lng, hover.lat]);
        if (!p) return null;
        return (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-xl bg-neutral-900 px-3 py-2 text-center text-white shadow-xl"
            style={{ left: `${(p[0] / W) * 100}%`, top: `${(p[1] / H) * 100}%` }}>
            <p className="whitespace-nowrap text-xs font-semibold">{hover.label}</p>
            <p className="text-[11px] font-bold text-ai">{hover.count} waiting</p>
          </motion.div>
        );
      })()}
    </div>
  );
}
